/**
 * Authentication Context
 * Manages Google OAuth authentication state
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

type Role = 'USER' | 'ADMIN';

interface User {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  role: Role;
  stats: {
    totalCreaturesSpawned: number;
    longestSurvival: number;
    mostOffspring: number;
    totalPlayTime: number;
  };
  currentCreatureId: string | null;
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Check authentication status
  const checkAuth = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        credentials: 'include',
        signal,
      });

      // Don't update state if unmounted or aborted
      if (!isMountedRef.current || signal?.aborted) return;

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          setUser(data.user);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') return;
      if (!isMountedRef.current) return;
      console.error('[Auth] Error checking auth:', error);
      setUser(null);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    abortControllerRef.current = new AbortController();
    checkAuth(abortControllerRef.current.signal);

    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, [checkAuth]);

  // Redirect to Google OAuth
  const login = useCallback(() => {
    window.location.href = `${API_URL}/auth/google`;
  }, []);

  // Logout
  const logout = useCallback(async () => {
    // Abort any pending auth checks to prevent race conditions
    abortControllerRef.current?.abort();

    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('[Auth] Logout error:', error);
    } finally {
      setUser(null);
    }
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    // Abort previous request and create new controller
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    await checkAuth(abortControllerRef.current.signal);
  }, [checkAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'ADMIN',
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
